/**
 * Panhandler JavaScript library.
 *
 * Encrypts data using Panhandler's public key.
 *
 * Before encryption, the plaintext data must be in a specific JSON format:
 * {
 *   "card_number": "4111111111111111",
 *   "expiry": {
 *     "month": "11",
 *     "year": "16"
 *   },
 *   "cvv": "111",
 *   "postal_code": "94111"
 * }
 *
 * We use the following JWE header:
 * {
 *   "cty": "application/json",
 *   "kid": PUBLIC_KEY_ID,
 *   "enc": "A128GCM",
 *   "alg": "RSA-OAEP"
 * }
 *
 * Once encrypted, the data looks like:
 * eyJjdHkiOiJhcHBsaWNhdGlvbi9qc29uIiwia2lkIjoiTW1Rek5XRXdZVFEyTVdRMlptWXdaRE  ᔥ
 * ZqTWpNeVlXUTBZek15WW1SbVpqazVaVGhpT0RBMk1YeHdZVzVvWVc1a2JHVnlmR05aYm1WS01r  ш
 * WTRkMEV6TkVOZmN6RkVhSFpYZG0xQk4zSm1MVkZqZGtFemJrdHZjV1ZQVW1KRlVXTSIsImFsZy  ⊢
 * I6IlJTQS1PQUVQIiwiZW5jIjoiQTEyOEdDTSJ9.TT2dwAUuAH9xl7FT7tcJWuWrWmkhvO1Qi-l  ᚛
 * 9-GmXlw8UFqI4zHklf2pyKO52BxshQcgoJ_3v2kQbwC0YBmqsTxaJRfD2iuskzEWtxSxzw6n3N  ∞
 * VsPxXD-SQKvFUI-lM3N39-WxtGjFa2kYXuUxVGLAbxE0Z7i6pyatSunY9wyTsZGtKRFlrvSdfU  ᚛
 * TwOouQQDAhB-rD26vqndYpkmidSS7ML3kmciOgtGXLyhUsTeuKy5lwTSKta79SkCgjOCvoEIv-  Z
 * cMrHViMlRn76m-9SvUSxI95SivQzD9Ys-qClw8dvb5WxU4aC-xsjI7_u5XmRrWheVgCJQdpNBs  ᗉ
 * x-IN2vpXiiA.EU-dod5IY58gnETo.O9Z11d_80PgOoZGirfD0DwKzn-XSBoeMCJPvFhYkyIJQl  ᓬ
 * uf4Gtbg5P0c3HxuePIjKMUzbiJxc-T0JScT37-QQth-WMezLtqhwm38Gdu4TdF_6XEvTn2iJvH  o
 * P_VvHhneVTDzr3EjYApk.zIJJLeZ1-mLlesStSuuf3Q                                 ᔥ
 *
 * If you are using this library as part of form submission process, you must
 * make sure to exclude or strip the plain text version of the card data from
 * the data that gets sent to the server in the POST request.
 */

import { JoseJWE, WebCryptographer, Utils, caniuse } from 'jose-jwe';
export default function Panhandler(cardNumber, expiryMonth, expiryYear, cvv, postalCode, useProductionKeys) {
  this.cardData = {
    card_number: cardNumber,
    expiry: {
      month: expiryMonth,
      year: expiryYear
    },
    cvv,
    postal_code: postalCode
  };
  const algorithm = 'RSA-OAEP';
  if (useProductionKeys) {
    this.publicRSAKey = Utils.importRsaPublicKey({
      n: '00:e3:b4:8a:ff:8b:b0:a4:0f:17:0f:f8:66:4a:47:a2:fe:c7:45:92:9c:d4:65:11:2a:d3:d1:7b:ce:a5:e7:3d:7a:64:1f:23:8a:cd:ee:c2:0d:9d:11:b5:ce:4a:b5:36:6d:2e:a5:6c:4f:c7:ee:fd:e6:43:a3:a2:ae:ee:f6:9c:17:d3:a9:41:f3:44:c0:74:57:07:b3:ac:c7:5e:a6:9c:a9:72:2d:9c:cd:10:7e:c0:0a:9a:11:97:0e:dc:c6:b2:0e:30:ac:69:98:68:b4:99:78:02:7b:04:72:c6:fe:1b:d2:d6:04:04:96:76:63:4c:2b:5d:28:1a:b0:60:12:2a:c5:50:b6:cc:f8:0c:0f:de:b3:f6:cc:25:89:7b:79:ce:01:d8:e3:c8:c9:70:b2:63:34:b0:f7:50:f4:88:a7:be:97:8d:b6:c7:11:7b:20:80:27:be:d2:c2:46:a3:84:57:ef:17:89:d2:c3:f4:fc:06:4b:a4:65:d8:86:2c:29:2f:c6:72:9e:37:9b:55:2e:e6:6e:58:a2:0c:60:f0:78:de:5d:69:3d:55:ba:0d:d4:f8:d0:79:ff:10:84:c0:ac:79:87:00:90:a2:79:77:bf:1e:20:57:d4:f0:76:3e:7b:f9:4f:a5:75:f2:b5:6d:37:b1:ab:78:44:16:ef:64:dc:b8:21',
      e: 65537
    }, algorithm);
    this.publicKeyID = 'OTBhN2M0NDcwMjZiZGQ1OWQyZTAzNmI5ODk4MjE0NDJjMGJkZWViNHxwYW5oYW5kbGVyfF9uY1FWN3pQdUhsbGlfVW9iNWlrTGJWQWdfbGlYYTlwTElGVEpyMEptNHc';
  } else {
    this.publicRSAKey = Utils.importRsaPublicKey({
      n: '00:9d:ee:5f:53:83:c3:bd:85:0f:fd:00:bb:b7:b2:2f:99:55:19:0f:00:99:ff:d4:e1:9f:b1:a6:d5:39:f6:c1:ac:5d:9a:a7:9d:e7:f7:2f:ad:fd:c3:32:b3:97:74:42:65:87:e8:cd:c8:ca:c6:a2:34:ad:c1:8a:e3:69:48:a2:06:44:f5:5f:3d:97:e0:93:52:e3:02:8d:e6:42:30:b7:84:b6:fa:e3:8d:9d:fc:4f:90:b5:1d:bf:39:28:3b:64:b5:cb:3f:01:4d:4d:4a:88:71:ad:0e:17:c3:d9:8b:e4:9c:02:24:41:12:12:5c:0b:ac:04:61:2e:98:2c:c6:4a:90:cf:47:24:fe:26:9c:d1:92:ec:35:df:17:e2:71:4c:76:97:f4:ce:dd:7c:4b:7d:04:59:65:19:8e:23:0f:ec:89:07:24:0a:36:8e:a9:f1:e0:92:15:66:e6:66:b8:fb:72:95:84:68:de:79:43:c6:34:a8:3a:50:9b:40:cb:b0:0e:49:cb:fe:a9:1c:9b:d8:9e:3d:dc:3a:1b:d9:bb:56:a6:d9:d6:39:31:e3:30:84:46:cb:4f:d3:87:aa:d4:0c:5e:92:0d:69:2d:93:41:ea:1f:3f:98:b1:e5:b3:27:79:44:0d:ce:54:81:99:65:0b:a4:12:e4:14:f5:1a:19:b8:df',
      e: 65537
    }, algorithm);
    this.publicKeyID = 'MmQzNWEwYTQ2MWQ2ZmYwZDFjMjMyYWQ0YzMyYmRmZjk5ZThiODA2MXxwYW5oYW5kbGVyfGNZbmVKMkY4d0EzNENfczFEaHZXdm1BN3JmLVFjdkEzbktvcWVPUmJFUWM';
  }
}

/**
 * Encrypts this.cardData.
 *
 * Asynchronously calls callback with the cipher text.
 * Throws an Error if JoseJWE is not supported.
 */
Panhandler.prototype.encrypt = function (callback) {
  if (!caniuse()) {
    throw new Error('JoseJWE is not supported in this browser.');
  }
  const cryptographer = new WebCryptographer();
  cryptographer.setContentEncryptionAlgorithm('A128GCM');
  const encrypter = new JoseJWE.Encrypter(cryptographer, this.publicRSAKey);
  encrypter.addHeader('cty', 'application/json');
  encrypter.addHeader('kid', this.publicKeyID);
  encrypter.encrypt(JSON.stringify(this.cardData)).then(function (result) {
    callback(result);
  });
};